/**
 * The visitor's choice about saving calculator inputs in the browser.
 *
 * The website (portfolio page and financial calculator) sets no cookies and has no tracking. The
 * only thing it can store is the visitor's own calculator inputs and settings (localStorage), so
 * they are still there on the next visit. Nothing is saved until the visitor allows it, and
 * "don't save" deletes anything stored before. One choice covers the whole site, because all
 * pages share the same origin and therefore the same browser storage.
 *
 * The choice itself is remembered under CONSENT_KEY; keeping that record is needed to honour
 * the choice and is the only entry written when the visitor declines.
 */
export type StorageChoice = 'granted' | 'denied';

export const CONSENT_KEY = 'gfc_storage_consent';
/** Bump when the banner text or what is stored changes, so visitors are asked again. */
export const CONSENT_VERSION = 1;

interface ConsentRecord {
  choice: StorageChoice;
  version: number;
  /** ISO timestamp of the decision. */
  date: string;
}

const listeners = new Set<() => void>();
let cached: StorageChoice | null | undefined;

function read(): StorageChoice | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const record = JSON.parse(raw) as Partial<ConsentRecord>;
    if (record.version !== CONSENT_VERSION) return null;
    return record.choice === 'granted' || record.choice === 'denied' ? record.choice : null;
  } catch {
    return null; // storage blocked or corrupted: treat as "not decided"
  }
}

/** The current choice, or null while the visitor has not decided (or was asked again). */
export function getStorageConsent(): StorageChoice | null {
  if (cached === undefined) cached = read();
  return cached;
}

export function setStorageConsent(choice: StorageChoice): void {
  const record: ConsentRecord = { choice, version: CONSENT_VERSION, date: new Date().toISOString() };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
  } catch {
    // Storage blocked: the choice still applies for this visit.
  }
  cached = choice;
  listeners.forEach((fn) => fn());
}

export function subscribeStorageConsent(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** For tests: forget the in-memory copy so the next read comes from storage. */
export function resetStorageConsentCache(): void {
  cached = undefined;
}

// ── Reopening the banner ("Cookie settings" link) ─────────────────────────────
let settingsOpen = false;

/** True while the visitor has reopened the banner to change an earlier choice. */
export function isStorageSettingsOpen(): boolean {
  return settingsOpen;
}

export function openStorageSettings(): void {
  settingsOpen = true;
  listeners.forEach((fn) => fn());
}

export function closeStorageSettings(): void {
  settingsOpen = false;
  listeners.forEach((fn) => fn());
}
